import { parseImageFromFile, resizeImage, useToast } from "@faro-lotv/flat-ui";
import { assert, getFileExtension } from "@faro-lotv/foundation";
import * as PDFJS from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { useCallback, useState } from "react";
import {
  MAX_ALLOWED_IMG_HEIGHT,
  MAX_ALLOWED_IMG_WIDTH,
} from "./create-area-utils";

// Deploy and register the pdfjs worker
PDFJS.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// The default page to render when a PDF is selected and the user has not selected a specific page
export const DEFAULT_PDF_PAGE_TO_RENDER = 1;

// The length in pixels of the larger side of the preview image
const PREVIEW_IMAGE_LARGER_SIDE_LENGTH = 2048;

export type UseParseUserImageReturn = {
  /** The preview image file extracted by extractPreviewImage function with the user selected file and page number */
  previewImageFile?: File;

  /** True, if the image is larger than supported limits */
  isImageOversized: boolean;

  /**
   * Extracts a preview image from the provided file and optional page number.
   *
   * @param file - The file to extract the preview image from.
   * @param pageNumber - The page number to extract (for PDFs) or undefined for image files.
   * @param largerSideLength - (Optional) The desired length for the larger side of the preview image in pixels.
   *   If the value exceeds `MAX_ALLOWED_IMG_WIDTH` or `MAX_ALLOWED_IMG_HEIGHT`, it will be changed
   *   to keep the extracted image size within the allowed dimensions.
   */
  extractPreviewImage(
    file?: File,
    pageNumber?: number,
    largerSideLength?: number,
  ): void;

  /** total number of pages in PDF document; zero if file is not *.pdf */
  numberOfPdfPages?: number;

  /** ratio width/height of original image - used to scale rotated image in preview  */
  widthToHeightRatio: number;

  /**
   * Extracts an image from the provided file.
   *
   * @param file - The file to extract the image from.
   * @param pageNumber - The page number to extract (for PDFs) or undefined for image files.
   * @param largerSideLength - (Optional) The desired length for the larger side of the preview image in pixels.
   *   If the value exceeds `MAX_ALLOWED_IMG_WIDTH` or `MAX_ALLOWED_IMG_HEIGHT`, it will be changed
   *   to keep the extracted image size within the allowed dimensions.
   * @returns A promise that resolves to the extracted image file or undefined if extraction fails.
   */
  extractImageFromFile(
    file: File,
    pageNumber?: number,
    largerSideLength?: number,
  ): Promise<ExtractImageData | undefined>;
};

/** Describes the data for an image file */
type ExtractedDataFromImageFile = {
  /** Indicates if the file processed is an image file (jpeg, png, etc.). It will always be true as this is the result for image files*/
  isImageFile: true;
  /** The image file extracted from the original file */
  file: File;
  /** The aspect ratio of the extracted image */
  aspectRatio: number;
  /** Indicates if the resolution of the extracted image is reduced compared to the original file */
  isResolutionReduced: boolean;
  /** The width of the extracted image in pixels */
  width: number;
  /** The height of the extracted image in pixels */
  height: number;
};

/** Describes the data for a PDF file */
type ExtractedDataFromPdfFile = Pick<
  ExtractedDataFromImageFile,
  "file" | "aspectRatio"
> & {
  /** Indicates if the file processed is an image file (jpeg, png, etc.). It will always be false as this is the result for PDF files*/
  isImageFile: false;
  /** The total number of pages in the PDF document processed */
  numberOfPages: number;
};

/** Describes the data for an extracted image from a file */
type ExtractImageData = ExtractedDataFromImageFile | ExtractedDataFromPdfFile;

/**
 * Extracts image data from a given image file, optionally resizing it if it exceeds maximum allowed dimensions.
 *
 * @param imageFile - The image file to extract data from.
 * @param largerSideLength - (Optional) The desired length for the larger side of the image when resizing.
 * @returns A promise that resolves to an object containing the extracted image data, including the (possibly resized)
 * file, aspect ratio, and resolution reduction status.
 * Note:
 * - If the image exceeds `MAX_ALLOWED_IMG_WIDTH` or `MAX_ALLOWED_IMG_HEIGHT`, it will be resized to fit within the allowed dimensions.
 * - The returned object indicates whether the image was resized via the `isResolutionReduced` property.
 */
async function extractImageFromImageFile(
  imageFile: File,
  largerSideLength?: number,
): Promise<ExtractedDataFromImageFile> {
  const { width, height } = await parseImageFromFile(imageFile);
  const aspectRatio = width / height;
  const maxAllowedSize = Math.max(
    MAX_ALLOWED_IMG_WIDTH,
    MAX_ALLOWED_IMG_HEIGHT,
  );
  const finalSize = largerSideLength
    ? Math.min(largerSideLength, maxAllowedSize)
    : maxAllowedSize;

  if (width > finalSize || height > finalSize) {
    // The user uploaded image has exceeded the maximum allowed size, hence resizing the image
    const compressedFile = await resizeImage(imageFile, {
      maxSize: finalSize,
    });
    return {
      isImageFile: true,
      file: compressedFile,
      aspectRatio,
      isResolutionReduced: true,
      width,
      height,
    };
  }

  return {
    isImageFile: true,
    file: imageFile,
    aspectRatio,
    isResolutionReduced: false,
    width,
    height,
  };
}

/**
 * Provides a local state to capture the user selected file, and convert it to
 * a format that can be uploaded as a floor image to the project
 *
 * @returns an instance of the UseParseUserImageReturn with the data to use in the component
 */
export function useParseUserImage(): UseParseUserImageReturn {
  const [previewImageFile, setPreviewImageFile] = useState<File>();
  const [isImageOversized, setIsImageOversized] = useState<boolean>(false);
  const [numberOfPdfPages, setNumberOfPdfPages] = useState<number | undefined>(
    undefined,
  );
  const [widthToHeightRatio, setWidthToHightRatio] = useState(1);
  const { openToast } = useToast();

  const extractImageFromFile = useCallback(
    async (
      file: File,
      pageNumber?: number,
      largerSideLength?: number,
    ): Promise<ExtractImageData | undefined> => {
      try {
        // If the input image is not a PDF
        if (getFileExtension(file.name)?.toLowerCase() !== "pdf") {
          return await extractImageFromImageFile(file, largerSideLength);
        }

        // Otherwise if the input file is a PDF
        return await extractImageFromPdfPage(
          file,
          pageNumber ?? DEFAULT_PDF_PAGE_TO_RENDER,
          largerSideLength,
        );
      } catch {
        openToast({
          title:
            "Extracting a floor image from the selected file failed, please try with a different file",
          variant: "error",
          persist: false,
        });
      }
    },
    [openToast],
  );

  const extractPreviewImage = useCallback(
    async (input?: File, pageNumber?: number) => {
      setWidthToHightRatio(1);

      // Input will be undefined if the selected image is reset
      if (!input) {
        setPreviewImageFile(undefined);
        return;
      }

      const extractedData = await extractImageFromFile(
        input,
        pageNumber,
        PREVIEW_IMAGE_LARGER_SIDE_LENGTH,
      );
      if (extractedData) {
        setPreviewImageFile(extractedData.file);
        setWidthToHightRatio(extractedData.aspectRatio);
        if (extractedData.isImageFile) {
          setNumberOfPdfPages(undefined);
          const isImageTooLarge =
            extractedData.width > MAX_ALLOWED_IMG_WIDTH ||
            extractedData.height > MAX_ALLOWED_IMG_HEIGHT;
          setIsImageOversized(isImageTooLarge);
        } else {
          setNumberOfPdfPages(extractedData.numberOfPages);
        }
      }
    },
    [extractImageFromFile],
  );

  return {
    previewImageFile,
    isImageOversized,
    widthToHeightRatio,
    extractPreviewImage,
    numberOfPdfPages,
    extractImageFromFile,
  };
}

/**
 * Convert specific page of a pdf file to a png file
 *
 * @param input the pdf file
 * @param pageNumber the page number for which an image should be extracted
 * @param largerSideLength the desired length in pixels that the extracted image larger side should have.
 *   This value is capped to ${MAX_ALLOWED_IMG_WIDTH} pixels.
 * @returns an object containing the image file extracted from the PDF and some additional data
 */
async function extractImageFromPdfPage(
  input: File,
  pageNumber: number,
  largerSideLength?: number,
): Promise<ExtractedDataFromPdfFile> {
  // Read the file content and parse it
  const content = await input.arrayBuffer();
  const pdfDoc = await PDFJS.getDocument(content).promise;

  const { numPages } = pdfDoc;

  assert(pageNumber <= numPages, "Invalid page requested");
  const page = await pdfDoc.getPage(pageNumber);

  // Get the original viewport at scale = 1
  const viewport = page.getViewport({ scale: 1 });

  const wantedSize = largerSideLength
    ? Math.min(largerSideLength, MAX_ALLOWED_IMG_WIDTH)
    : undefined;

  // Calculate the scale to achieve the desired size matching to max supported width/height (use one which is bigger)
  const scale =
    viewport.width > viewport.height
      ? (wantedSize ?? MAX_ALLOWED_IMG_WIDTH) / viewport.width
      : (wantedSize ?? MAX_ALLOWED_IMG_HEIGHT) / viewport.height;

  // Create a new viewport with the calculated scale
  const scaledViewport = page.getViewport({ scale });

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  assert(
    ctx,
    "Unable to create an offscreen 2d context to render the pdf image",
  );

  // Set canvas dimensions based on the scaled viewport
  canvas.width = scaledViewport.width;
  canvas.height = scaledViewport.height;
  const aspectRatio = scaledViewport.width / scaledViewport.height;

  // Render and convert the pdf page to a jpeg blob
  await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
        return;
      }
      reject(new Error("Unable to convert pdf to image"));
    }),
  );

  return {
    isImageFile: false,
    file: new File([blob], input.name.replace(".pdf", ".png"), {
      type: "image/png",
    }),
    aspectRatio,
    numberOfPages: numPages,
  };
}
